import {
  ProxyControlInputSchema,
  PROXY_CONTROL_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { readProxyStatusSafely } from '../../../../utils/readProxyStatusSafely';
import { formatUpstreamProxyForIpc } from '../../../../electron-shared/utils/proxy.util';
import { useProxyStore } from '../../../../store/proxy-store';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

export const proxyControlHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: ProxyControlInputSchema,
    input,
    toolName: PROXY_CONTROL_TOOL_NAME,
    toolCallId,
    batchId,
  });

  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable()) {
    return getElectronApiUnavailableResult({
      toolName: PROXY_CONTROL_TOOL_NAME,
      toolCallId,
      batchId,
    });
  }

  const { settings, isProxyRunning, setProxyRunning } =
    useProxyStore.getState();

  switch (parsedInput.data.action) {
    case 'start': {
      const result = await window.electronAPI.proxy.start({
        host: settings.host,
        port: settings.port,
        interceptEnabled: settings.interceptEnabled,
        enableHttpsInterception: settings.enableHttps,
        httpsPort: settings.httpsPort,
        autoGenerateCerts: settings.autoGenerateCerts,
        certificatePath: settings.certificatePath,
        keyPath: settings.keyPath,
        caCertPath: settings.caCertPath,
        upstreamProxy: formatUpstreamProxyForIpc({
          upstreamEnabled: settings.upstreamEnabled,
          upstreamHost: settings.upstreamHost,
          upstreamPort: settings.upstreamPort,
        }),
        upstreamAuth:
          settings.upstreamEnabled && settings.upstreamAuth?.username
            ? {
                username: settings.upstreamAuth.username,
                password: settings.upstreamAuth.password ?? '',
              }
            : undefined,
        validateUpstreamTls: settings.validateUpstreamTls,
      });

      if (!result.success) {
        const realStatus = await readProxyStatusSafely();

        if (realStatus?.isRunning) {
          setProxyRunning(true);

          return {
            status: 'success',
            result: {
              message: `Proxy already running on port ${realStatus.port ?? settings.port}`,
              data: {
                isRunning: true,
                port: realStatus.port ?? settings.port,
                httpsPort: realStatus.httpsPort ?? settings.httpsPort,
                interceptEnabled: settings.interceptEnabled,
                matchReplaceEnabled: settings.matchReplaceEnabled,
              },
            },
          };
        }

        setProxyRunning(false);

        return {
          status: 'error',
          result: {
            message: result.error || 'Failed to start proxy',
          },
        };
      }

      setProxyRunning(true);

      return {
        status: 'success',
        result: {
          message: `Proxy started on port ${result.port ?? settings.port}`,
          data: {
            isRunning: true,
            port: result.port ?? settings.port,
            httpsPort: settings.httpsPort,
            interceptEnabled: settings.interceptEnabled,
            matchReplaceEnabled: settings.matchReplaceEnabled,
          },
        },
      };
    }

    case 'stop': {
      const result = await window.electronAPI.proxy.stop();

      if (!result.success) {
        return {
          status: 'error',
          result: {
            message: result.error || 'Failed to stop proxy',
          },
        };
      }

      setProxyRunning(false);

      return {
        status: 'success',
        result: {
          message: 'Proxy stopped',
          data: { isRunning: false, port: null, httpsPort: null },
        },
      };
    }

    case 'status': {
      const statusResult = await readProxyStatusSafely();
      const running = statusResult?.isRunning ?? isProxyRunning;

      if (running !== isProxyRunning) {
        setProxyRunning(running);
      }

      return {
        status: 'success',
        result: {
          message: running ? 'Proxy is running' : 'Proxy is stopped',
          data: {
            isRunning: running,
            port: statusResult?.port ?? null,
            httpsPort: statusResult?.httpsPort ?? null,
          },
        },
      };
    }

    case 'get-settings': {
      return {
        status: 'success',
        result: {
          message: 'Retrieved proxy settings summary',
          data: {
            isRunning: isProxyRunning,
            port: settings.port,
            httpsPort: settings.httpsPort,
            interceptEnabled: settings.interceptEnabled,
            matchReplaceEnabled: settings.matchReplaceEnabled,
          },
        },
      };
    }

    default: {
      return {
        status: 'error',
        result: { message: `Unknown action for ${PROXY_CONTROL_TOOL_NAME}` },
      };
    }
  }
};
